/**
 * NetworkConnectionsPage — Manage sent / received connection requests
 * Route: /network/connections
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  UserCheck, UserX, Trash2, RefreshCw, Clock,
  CheckCircle2, XCircle, Building2, MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { networkConnectApi, type NetworkConnection } from '@/lib/network-api';
import { cn } from '@/lib/utils';

const STATUS_LABEL: Record<string, string> = {
  active:   'Connected',
  pending:  'Pending',
  declined: 'Declined',
  revoked:  'Revoked',
};

const STATUS_COLOR: Record<string, string> = {
  active:   'border-green-500 text-green-600',
  pending:  'text-amber-600 border-amber-400',
  declined: 'text-red-500 border-red-400',
  revoked:  'text-muted-foreground',
};

const CONN_TYPE_LABEL: Record<string, string> = {
  partner:  'Partner',
  vendor:   'Vendor',
  customer: 'Customer',
  agent:    'Agent',
};

// ─── Connection row ───────────────────────────────────────────────────────────

function ConnectionRow({
  conn,
  isIncoming,
  onAccept,
  onDecline,
  onRevoke,
  onMessage,
}: {
  conn: NetworkConnection;
  isIncoming?: boolean;
  onAccept?: (id: string) => void;
  onDecline?: (id: string) => void;
  onRevoke?: (id: string) => void;
  onMessage?: (memberId: string) => void;
}) {
  const peer = conn.peer_member;
  const peerName = peer?.company_name ?? '(Unknown)';

  return (
    <div className="flex items-start gap-4 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-col items-center gap-1 pt-0.5">
        <div className="flex size-10 items-center justify-center rounded-full bg-muted">
          <Building2 className="size-5 text-muted-foreground" />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground">{peerName}</span>
          <Badge variant="outline" className={cn('text-xs', STATUS_COLOR[conn.status])}>
            {STATUS_LABEL[conn.status] ?? conn.status}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {CONN_TYPE_LABEL[conn.connection_type] ?? conn.connection_type}
          </Badge>
        </div>
        {conn.introduction_message && (
          <p className="text-sm text-muted-foreground line-clamp-2">"{conn.introduction_message}"</p>
        )}
        <p className="text-xs text-muted-foreground">
          {isIncoming ? 'Received' : 'Sent'} {new Date(conn.created_at).toLocaleDateString('en-SG')}
          {conn.responded_at && ` · Responded ${new Date(conn.responded_at).toLocaleDateString('en-SG')}`}
        </p>
      </div>

      <div className="flex shrink-0 flex-col gap-1.5 items-end">
        {isIncoming && conn.status === 'pending' && (
          <>
            <Button size="sm" onClick={() => onAccept?.(conn.id)}>
              <UserCheck className="mr-1.5 size-3.5" />Accept
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecline?.(conn.id)}>
              <UserX className="mr-1.5 size-3.5" />Decline
            </Button>
          </>
        )}
        {conn.status === 'active' && (
          <>
            {peer && (
              <Button size="sm" variant="outline" onClick={() => onMessage?.(peer.id)}>
                <MessageSquare className="mr-1.5 size-3.5" />Message
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => onRevoke?.(conn.id)}
            >
              <Trash2 className="mr-1.5 size-3.5" />Remove
            </Button>
          </>
        )}
        {!isIncoming && conn.status === 'pending' && (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={() => onRevoke?.(conn.id)}
          >
            <XCircle className="mr-1.5 size-3.5" />Revoke
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NetworkConnectionsPage() {
  const navigate = useNavigate();
  const [active, setActive]   = useState<NetworkConnection[]>([]);
  const [pending, setPending] = useState<NetworkConnection[]>([]);
  const [sent, setSent]       = useState<NetworkConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, pendingRes, sentRes] = await Promise.all([
        networkConnectApi.list({ status: 'active' }),
        networkConnectApi.pending(),
        networkConnectApi.list({ status: 'pending' }),
      ]);
      setActive(activeRes.items);
      setPending(pendingRes.items);
      // "sent" = pending where this tenant is the sender (no peer_received field, so filter by direction)
      // The API returns pending from my side when hitting /connect?status=pending
      // pending endpoint returns received; list?status=pending returns all pending for me
      // We'll treat /connect/pending as "received" and list?status=pending minus received as "sent"
      const receivedIds = new Set(pendingRes.items.map((c) => c.id));
      setSent(sentRes.items.filter((c) => !receivedIds.has(c.id)));
    } catch {
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleAccept(id: string) {
    try {
      await networkConnectApi.accept(id);
      toast.success('Connection accepted');
      load();
    } catch {
      toast.error('Operation failed');
    }
  }

  async function handleDecline(id: string) {
    try {
      await networkConnectApi.decline(id);
      toast.success('Connection request declined');
      setPending((p) => p.filter((c) => c.id !== id));
    } catch {
      toast.error('Operation failed');
    }
  }

  async function handleRevoke(id: string) {
    try {
      await networkConnectApi.revoke(id);
      toast.success('Connection removed');
      setActive((p) => p.filter((c) => c.id !== id));
      setSent((p) => p.filter((c) => c.id !== id));
    } catch {
      toast.error('Operation failed');
    } finally {
      setRevokeId(null);
    }
  }

  function handleMessage(memberId: string) {
    navigate(`/network/messages/${memberId}`);
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold">My Connections</h1>
          <p className="text-sm text-muted-foreground">Manage your company connections on Boox Network</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/network/directory')}>
            Discover
          </Button>
          <Button variant="ghost" size="icon" onClick={load}>
            <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="active" className="flex flex-1 flex-col overflow-hidden">
        <TabsList className="shrink-0 mx-6 mt-4 w-fit">
          <TabsTrigger value="active">
            <CheckCircle2 className="mr-1.5 size-3.5" />
            Connected {active.length > 0 && `(${active.length})`}
          </TabsTrigger>
          <TabsTrigger value="pending">
            <Clock className="mr-1.5 size-3.5" />
            Pending {pending.length > 0 && `(${pending.length})`}
          </TabsTrigger>
          <TabsTrigger value="sent">
            Sent {sent.length > 0 && `(${sent.length})`}
          </TabsTrigger>
        </TabsList>

        {/* Active */}
        <TabsContent value="active" className="flex-1 overflow-y-auto px-6 py-4">
          {loading && active.length === 0 ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : active.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <Building2 className="size-10 opacity-30" />
              <p>No active connections yet</p>
              <Button variant="outline" onClick={() => navigate('/network/directory')}>Discover Companies</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {active.map((c) => (
                <ConnectionRow
                  key={c.id}
                  conn={c}
                  onRevoke={(id) => setRevokeId(id)}
                  onMessage={handleMessage}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pending received */}
        <TabsContent value="pending" className="flex-1 overflow-y-auto px-6 py-4">
          {loading && pending.length === 0 ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : pending.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">No pending connection requests</div>
          ) : (
            <div className="flex flex-col gap-3">
              {pending.map((c) => (
                <ConnectionRow
                  key={c.id}
                  conn={c}
                  isIncoming
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Sent */}
        <TabsContent value="sent" className="flex-1 overflow-y-auto px-6 py-4">
          {loading && sent.length === 0 ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : sent.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">No outgoing connection requests</div>
          ) : (
            <div className="flex flex-col gap-3">
              {sent.map((c) => (
                <ConnectionRow
                  key={c.id}
                  conn={c}
                  onRevoke={(id) => setRevokeId(id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Revoke confirm */}
      <ConfirmDialog
        open={!!revokeId}
        title="Remove Connection?"
        description="Once removed, you will no longer be able to message each other. A new connection request will be required."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => revokeId && handleRevoke(revokeId)}
        onCancel={() => setRevokeId(null)}
      />
    </div>
  );
}
